import get from 'lodash/get';
import omit from 'lodash/omit';
import { createReducer } from '@reduxjs/toolkit';

import {
  ActionFailureType,
  ActionSuccessType,
  CLEAR_AUTH_TOKEN,
  CLEAR_MFA_CHALLENGE,
  CLEAR_SUBMITED_SIGNED_IN_SIGNAL,
  CONFIRM_USER,
  CURRENT_USER,
  INITIALIZE_USER_TOKEN,
  REFRESH_AUTH_TOKEN,
  SET_MFA_CHALLENGE,
  SHOW_CONFIRM_NAVIGATION_MODAL,
  SIGN_IN,
  SIGN_IN_WITH_TOKEN,
  SIGN_OUT,
  USER_RESET_PASSWORD,
} from 'constants/redux-actions';
import AxiosClient from 'configurations/api-client';
import { API_SIGN_OUT } from 'constants/api-paths';
import { createOAuthSession, deleteOAuthSession } from 'utils/oauth-session';
import { isMfaChallengeResponse, mfaChallengeFromResponse, mfaStepUpFromResponse } from 'utils/mfa';
import { failedWithCurrentToken } from 'utils/authentication';

const initState = {};

// A challenge parks the sign-in until the sign-in page answers it; the token only lands afterwards
const setMfaChallenge = (prevState, data) => ({ ...prevState, mfaChallenge: mfaChallengeFromResponse(data) });

const signInSuccess = (prevState, action) => {
  const data = get(action, 'payload.data', {});

  if (isMfaChallengeResponse(data)) return setMfaChallenge(prevState, data);

  createOAuthSession(data.token);

  return {
    ...omit(prevState, 'mfaChallenge'),
    token: data.token,
    // Kept so a later step-up can ask for the same session length; a bare token refresh keeps the current one
    rememberMe: data.rememberMe ?? prevState.rememberMe ?? false,
    successfulSignIn: true,
  };
};

const authReducer = createReducer(initState, {
  [ActionSuccessType(SIGN_IN)]: signInSuccess,
  [ActionSuccessType(SIGN_IN_WITH_TOKEN)]: signInSuccess,
  [ActionFailureType(CURRENT_USER)]: (prevState, action) => {
    const isChallenge = isMfaChallengeResponse(get(action, 'error.response.data'));

    return isChallenge || !failedWithCurrentToken(action.error, prevState.token) ? prevState : initState;
  },
  [SIGN_OUT]: (prevState) => {
    AxiosClient.delete(API_SIGN_OUT, {
      headers: {
        Authorization: `Bearer ${prevState.token}`,
      },
    }).catch(() => {});
    deleteOAuthSession();

    return initState;
  },
  [ActionSuccessType(CONFIRM_USER)]: signInSuccess,
  [ActionSuccessType(USER_RESET_PASSWORD)]: signInSuccess,
  [SHOW_CONFIRM_NAVIGATION_MODAL]: (prevState, action) => ({
    ...prevState,
    showConfirmNavigationModal: action.payload,
  }),
  [CLEAR_SUBMITED_SIGNED_IN_SIGNAL]: (prevState) => omit(prevState, 'successfulSignIn'),
  [CLEAR_AUTH_TOKEN]: () => {
    deleteOAuthSession();
    return initState;
  },
  [INITIALIZE_USER_TOKEN]: signInSuccess,
  [SET_MFA_CHALLENGE]: (prevState, action) => ({
    ...prevState,
    mfaChallenge: mfaStepUpFromResponse(action.payload, prevState.mfaChallenge),
  }),
  [CLEAR_MFA_CHALLENGE]: (prevState) => omit(prevState, 'mfaChallenge'),
  [REFRESH_AUTH_TOKEN]: (prevState, action) => {
    createOAuthSession(action.payload);

    return { ...prevState, token: action.payload };
  },
});

export default authReducer;
